import { formatTime } from '../../utils/date.util'
import DocumentListing from '../common/DocumentListing'
import CollapsibleSection from '../common/CollapsibleSection'
import UserTypeDisplay from '../common/UserTypeDisplay'
import { CheckBadgeIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { VERIFICATION_STATUSES } from '../../constants/verificationStatuses'

export default function VerificationChecklist({
  verification,
  shopDocuments,
  hasVisitedShop,
  hasValidShopDetails,
}) {
  const isVisited = verification.visitedShop
  const visitedAt = verification.visitedShopAt

  const handleToggleVisited = () => {
    hasVisitedShop.mutate(!isVisited)
  }

  const renderIf = (condition, renderTrue = 'Done', renderFalse = 'Incompete') => {
    return condition ? (
      <>
        <CheckBadgeIcon className="w-6 h-6 text-green-600" />
        {renderTrue}
      </>
    ) : (
      <>
        <ExclamationCircleIcon className="w-6 h-6 text-error-900" />
        {renderFalse}
      </>
    )
  }

  return (
    <div className="space-y-6">
      <CollapsibleSection
        title="Shop Information"
        defaultExpanded={true}
        statusElement={renderIf(hasValidShopDetails)}
        description="Shop must have all required fields filled (name, description, address, etc)."
      >
        <div className="bg-background shadow-sm border border-gray-100 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Shop Name:</span>
            <div className=" text-gray-900">{verification?.shopName || '—'}</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">User:</span>
            <div className=" text-gray-900 flex gap-2 items-center">
              {verification?.name || '—'}

              <UserTypeDisplay userType={verification?.userType} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Phone:</span>
            <div className=" text-gray-900">{verification?.phoneNumber || '—'}</div>
          </div>

          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Location Verified:</span>
            <div className="">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  verification?.visitedShop
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {verification?.visitedShop ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Building Name:</span>
            <div className=" text-gray-900">{verification?.buildingName || '—'}</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Floor:</span>
            <div className=" text-gray-900">{verification?.floor || '—'}</div>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Shop Number:</span>
            <div className=" text-gray-900">{verification?.shopNumber || '—'}</div>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Documents"
        defaultExpanded={
          !verification?.hasUploadedAllRequiredDocuments || !verification?.hasAllDocsApproved
        }
        statusElement={renderIf(
          verification?.hasUploadedAllRequiredDocuments && verification?.hasAllDocsApproved
        )}
        description="All required shop documents must be reviewed and approved."
      >
        <DocumentListing
          documents={shopDocuments}
          readOnly={[
            VERIFICATION_STATUSES.approved.value,
            VERIFICATION_STATUSES.rejected.value,
          ].includes(verification?.status)}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Location Visit"
        defaultExpanded={!isVisited}
        statusElement={renderIf(isVisited)}
        description="The shop must be physically visited to verify its existence and location. The location details are as follows:"
      >
        <div className="text-sm text-gray-600 mb-4">
          <div>
            <span className="font-semibold">Floor:</span> {verification.floor || '—'}
          </div>
          <div>
            <span className="font-semibold">Building Name:</span> {verification.buildingName || '—'}
          </div>
          <div>
            <span className="font-semibold">Shop Number:</span> {verification.shopNumber || '—'}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={isVisited}
              onChange={handleToggleVisited}
              className="w-5 h-5 text-green-600 bg-background border-2 border-gray-300 rounded-md transition-all duration-200 ease-in-out "
            />
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
              Mark as visited
            </span>
          </label>
          <div className={`mt-2 text-sm text-gray-500 ${isVisited ? '' : 'invisible'}`}>
            Visited at: {formatTime(visitedAt)}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  )
}
